import React, {
	CSSProperties,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { classArr } from "../helper/style-helper";
import HoverTooltip from "./hover-tooltip";
import * as styles from "./cooldown-button.module.scss";
import whitelabel from "../whitelabel";

export interface CooldownButtonProps {
	style?: CSSProperties;
	className?: string;
	onPress?: () => void;
	title?: string;
	icon?: (size: number, color: string) => React.ReactNode;
	disabled?: boolean;
	//Tooltip text to display on hover
	tooltip?: string;
	//The number of milliseconds that this button will take for cooldown
	cooldown: number;
	//A key that when changed, will reset the current cooldown time
	cooldownKey?: unknown;
}

export default function CooldownButton(props: CooldownButtonProps) {
	const [isCooldown, setIsCooldown] = useState(false);
	const [progressWidth, setProgressWidth] = useState(100);
	const timeoutRef = useRef<number | null>(null);
	const rafRef = useRef<number | null>(null);

	const clearPending = useCallback(() => {
		if (timeoutRef.current !== null) {
			window.clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		if (rafRef.current !== null) {
			window.cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
	}, []);

	const startCooldown = useCallback(() => {
		clearPending();
		setIsCooldown(true);
		setProgressWidth(0);

		//Defer to the next frame so width transition runs from 0% to 100%
		rafRef.current = window.requestAnimationFrame(() => {
			setProgressWidth(100);
		});

		timeoutRef.current = window.setTimeout(() => {
			setIsCooldown(false);
			timeoutRef.current = null;
		}, props.cooldown);
	}, [clearPending, props.cooldown]);

	const onClick = useCallback(() => {
		if (props.disabled || isCooldown) return;
		startCooldown();
		props.onPress?.();
	}, [isCooldown, props.disabled, props.onPress, startCooldown]);

	useEffect(() => {
		clearPending();
		setIsCooldown(false);
		setProgressWidth(100);
	}, [clearPending, props.cooldownKey]);

	useEffect(() => clearPending, [clearPending]);

	return (
		<HoverTooltip wrapperClassName={styles.wrapper} tooltip={props.tooltip}>
			<button
				type="button"
				style={props.style}
				className={classArr(
					styles.button,
					isCooldown && styles.buttonActive,
					props.disabled && styles.buttonDisabled,
					props.className,
				)}
				onClick={onClick}
				disabled={props.disabled || isCooldown}
			>
				{isCooldown && (
					<div
						className={styles.progressBackground}
						style={{
							width: `${progressWidth}%`,
							transitionDuration: `${props.cooldown}ms`,
						}}
					/>
				)}

				<div className={styles.content}>
					{props.icon?.(24, whitelabel.palette.primary900)}
					{props.title && (
						<span className={styles.label}>{props.title}</span>
					)}
				</div>
			</button>
		</HoverTooltip>
	);
}
